import React from 'react';
import { DataTableModel, getApplicationListRespModel, RecordType } from "../../../Models/tableDetailsModel";
import { isString } from "../../../utils/checkIsString";
import { GetFormatTitle, GetFormattedData } from "./getFormattedData";
import { IAddEscalationFuncParams, IMenuOption, SortInfoModel } from '../../../Models/applicationModels';
import CustomSortIcons from '../sortIcon/sortIcons';
import { ColumnNameToExcludesFromTable, NotSortableColumns } from "./tableInfo";
import { Tooltip } from 'antd';
import OptionsMenu from "./ChildComponents/OptionMenu/OptionsMenu";
import { Box } from "@mui/material";
import EscalationIcons from "./ChildComponents/EscalationIcons/EscalationIcons";
import { UnLockIcon } from "./ChildComponents/LockIcon/UnLockIcon";
import { PiiRemoved } from './ChildComponents/piiRemoved/piiRemoved';
import { SsnLock } from './ChildComponents/ssnLock/ssnLock.index';  
import styles from './getTableColumn.module.css';
import { APPLICATION_DETAILS_LABEL_MAP, APPLICATION_DETAILS_TBL_LABEL_MAP, LAST_EVENT } from '../../../utils/constants/applicationDetailsBifurcation';
import { isSsnUnlocked } from '../../../utils/insightApplicationPage';
import { Link } from 'react-router-dom';
import { AGENTS_EDIT_ROUTE, EDIT_AGENT_QUERY_PARAM } from '../../../Pages/agents/agents.constants';

interface GetTableColumnsArgs {
    rowData: getApplicationListRespModel;
    sortInfo: SortInfoModel | null;
    menuOption: IMenuOption;
    handleChangeSort: (sortItem: SortInfoModel | null) => Promise<void>;
    handleLastEventClick: (opportunityId: string, firstName: string, lastName: string) => void;
    GetAnalysisResultApi: (opportunityId: string) => Promise<void>;
    handleNavigate: (opportunityId: string) => void;
    tableWidth: React.MutableRefObject<number>;
    HandleAddEscalation: (params: IAddEscalationFuncParams) => Promise<void>;
}


const GetTableColumns = (props: GetTableColumnsArgs): DataTableModel[] => {

    const {
        rowData,
        sortInfo,
        handleChangeSort,
        handleLastEventClick,
        tableWidth,
        handleNavigate,
        HandleAddEscalation,
        menuOption,
    } = props;

    const dataKeys = Object.keys(rowData);

    // An extra column for Edit is added at the end or the start
    // // Add column at the start of the columns.
    dataKeys.unshift("addEsc");
    // Remove column name you don't want to show in the table.
    let dataKeysToRender = dataKeys.filter((item) => !ColumnNameToExcludesFromTable.has(item));
    const fixedColumnOrder = ["addEsc", "tenantTitle", "agentName", "applicantName"];
    const fixedPresent = fixedColumnOrder.filter((k) => dataKeysToRender.includes(k));
    const rest = dataKeysToRender.filter((k) => !fixedColumnOrder.includes(k));
    dataKeysToRender = [...fixedPresent, ...rest];

    tableWidth.current = 0;


    return dataKeysToRender.map((key, index) => {

        let title = APPLICATION_DETAILS_TBL_LABEL_MAP[key] ?? APPLICATION_DETAILS_LABEL_MAP[key] ?? GetFormatTitle(key);

        const sortVisible = !NotSortableColumns.includes(key);

        let minWidthNum = isString(title) ? Math.max(title.length * 10, 100) : 100;
        // Add Extra width for the sort icon.
        minWidthNum = sortVisible ? minWidthNum + 20 : minWidthNum;

        tableWidth.current = tableWidth.current + (minWidthNum > 250 ? 250 : minWidthNum);
        const minWidth = String(minWidthNum) + 'px';

        const defaultReturn: DataTableModel = {
            key: key,
            dataIndex: key,
            title: <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}>
                {title}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                }}>
                    {sortVisible && <CustomSortIcons
                        column={key}
                        sortColumn={sortInfo?.value}
                        sortDescending={sortInfo?.sortDescending}
                        handleChangeSort={handleChangeSort}
                    />}
                </div>
            </div>,
            align: "left",
            render: (text: string, record: getApplicationListRespModel, index: number) => {
                const formattedData = GetFormattedData(text);
                const shouldWrapWithLink = false;

                return shouldWrapWithLink ? (
                    <a onClick={() => handleNavigate(record.opportunityUid as string)}
                        style={{ textDecoration: 'none', color: 'inherit' }}>
                        {formattedData}
                    </a>
                ) : (
                    <span>{formattedData}</span>
                );
            },
            ellipsis: false,
            onHeaderCell: () => ({
                style: {
                    backgroundColor: '#F8F9FA',
                    fontWeight: '600',
                    color: "#0F1419",
                    whiteSpace: 'nowrap',
                    minWidth,
                    width: minWidth,
                },
            }),
            onCell: (record, rowIndex) => ({
                style: {
                    backgroundColor: record.associatedEscalations.length > 0
                        ? "#fbedec"
                        : (rowIndex && rowIndex % 2 !== 0)
                            ? "#F6F6F6"
                            : "#fff",
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    maxWidth: '170px',
                    minWidth,
                },
            }),
            ...(index < 4 && { fixed: 'left' })
        };

        switch (key) {
            case 'applicationUrl':
                return {
                    ...defaultReturn,
                    align: "center",
                    render: (text: string, record: any, index: number) => {
                        if(!record.applicationUrl) return '-';
                        return (
                            <a target='_blank' href={text}>
                                <Tooltip title={text}>
                                    {text?.slice(0, 20) + "..."}
                                </Tooltip>
                            </a>
                        )
                    },
                };

            case 'addEsc':
                const actionMinWidth = 45;
                return {
                    ...defaultReturn,
                    fixed: 'left',
                    align: 'center',
                    title: <></>,
                    render: (_text: string, record: RecordType, _index: number) => {
                        const filteredOptions = {
                            ...menuOption,
                            escalation: []
                        };
                        const isSsnUnLocked = isSsnUnlocked(record);
                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                <OptionsMenu
                                    record={record}
                                    onViewDetails={handleNavigate}
                                    options={filteredOptions}
                                />
                                {record.isLocked && <UnLockIcon
                                    record={record}
                                />}
                                {(record.isSsnLocked || isSsnUnLocked) && 
                                    <SsnLock record={record} selfSsnLockedDetails={record?.selfSsnLockedDetails} />
                                }
                                {(record.associatedEscalations.length !== 0) && <EscalationIcons
                                    record={record}
                                    escalationIds={record.associatedEscalations}
                                    HandleAddEscalation={HandleAddEscalation}
                                    escalationNames={menuOption.escalation}
                                />}
                                {(record.isPIIRemoved) && <PiiRemoved />}
                            </Box>
                        );
                    },
                    onCell: (record: RecordType, rowIndex: number | undefined) => ({
                        style: {
                            ...defaultReturn.onCell(record, rowIndex).style,
                            minWidth: `${actionMinWidth}px`,
                        },
                    }),
                    onHeaderCell: (record: RecordType) => ({
                        style: {
                            ...defaultReturn.onHeaderCell(record).style,
                            minWidth: `${actionMinWidth}px`,
                            width: 'unset'
                        }
                    })
                };
            case LAST_EVENT:
                return {
                    ...defaultReturn,
                    render: (text: string, record: RecordType, _index: number) => (
                        <button onClick={() => handleLastEventClick(record.opportunityUid, record.firstName, record.lastName)} className={styles.clickableButton}>
                            {GetFormattedData(text)}
                        </button>
                    ),
                };
            case 'agentName':
                return {
                    ...defaultReturn,
                    render: (text: string, record: RecordType, _index: number) => (
                        <Link to={`${AGENTS_EDIT_ROUTE}?${EDIT_AGENT_QUERY_PARAM}=${record.userId}`} target='_blank' className={styles.clickableButton}>
                            {GetFormattedData(text)}
                        </Link>
                    ),
                };
        }

        return defaultReturn;
    });

};

export default GetTableColumns;